<script setup lang="ts">
import { proxyImageUrl } from '~/utils/imageProxy'
import type { Company } from '~~/server/types/company'

interface Props {
  company: Partial<Company> | null
  maxHeight?: string
  brandColor?: string
}
const props = defineProps<Props>()
const bgColor = computed(() => {
  const color = props.brandColor || '#5F0CE2'
  return color.replace(')', ', 0.2)').replace('rgb', 'rgba')
})
const showFullDescription = ref(false)
const descriptionLimit = 200
const truncatedDescription = computed(() => {
  if (!props.company?.description) return ''
  if (showFullDescription.value || props.company.description.length <= descriptionLimit) {
    return props.company.description
  }
  return props.company.description.substring(0, descriptionLimit) + '...'
})
const shouldShowSeeMore = computed(() => {
  return props.company?.description && props.company.description.length > descriptionLimit
})
const formatRevenue = (revenues: { [key: string]: number } | undefined) => {
  if (!revenues) return null
  const entries = Object.entries(revenues).sort(
    ([yearA], [yearB]) => Number(yearB) - Number(yearA)
  )
  if (entries.length === 0) return null
  const [latestYear, value] = entries[0]
  let formattedValue
  if (value >= 1e12) {
    formattedValue = `$${(value / 1e12).toFixed(1)}T`
  } else if (value >= 1e9) {
    formattedValue = `$${(value / 1e9).toFixed(1)}B`
  } else if (value >= 1e6) {
    formattedValue = `$${(value / 1e6).toFixed(1)}M`
  } else {
    formattedValue = `$${value.toLocaleString()}`
  }
  return `${formattedValue} (${latestYear})`
}
const companyDetails = computed(() => {
  const details = []
  if (props.company?.headquarters_location) {
    details.push({
      icon: 'i-heroicons-map-pin',
      label: 'Headquarters',
      value: props.company.headquarters_location
    })
  }
  if (props.company?.industry) {
    details.push({
      icon: 'i-heroicons-building-office',
      label: 'Industry',
      value: props.company.industry
    })
  }
  if (props.company?.size) {
    details.push({
      icon: 'i-heroicons-user-group',
      label: 'Company Size',
      value: `${props.company.size.toLocaleString()}+ employees`
    })
  }
  if (props.company?.year_founded) {
    details.push({
      icon: 'i-heroicons-calendar',
      label: 'Founded',
      value: props.company.year_founded.toString()
    })
  }
  const revenue = formatRevenue(props.company?.revenue_usd_per_year as { [key: string]: number })
  if (revenue) {
    details.push({
      icon: 'i-heroicons-currency-dollar',
      label: 'Revenue',
      value: revenue
    })
  }
  if (props.company?.ceo_name) {
    details.push({
      icon: 'i-heroicons-user',
      label: 'CEO',
      value: props.company.ceo_name
    })
  }
  if (props.company?.links?.website) {
    details.push({
      icon: 'i-heroicons-globe-alt',
      label: 'Website',
      value: props.company.links.website,
      isLink: true
    })
  }
  return details
})
const socialLinks = computed(() => {
  if (!props.company?.links) return []
  const supportedPlatforms = [
    { key: 'linkedin', icon: 'i-heroicons-link', label: 'LinkedIn' },
    { key: 'facebook', icon: 'i-heroicons-link', label: 'Facebook' },
    { key: 'instagram', icon: 'i-heroicons-link', label: 'Instagram' },
    { key: 'twitter', icon: 'i-heroicons-link', label: 'Twitter' },
    { key: 'x', icon: 'i-heroicons-link', label: 'X' },
    { key: 'youtube', icon: 'i-heroicons-link', label: 'YouTube' }
  ]
  return supportedPlatforms
    .filter(platform => props.company?.links?.[platform.key])
    .map(platform => ({
      ...platform,
      url: props.company?.links?.[platform.key]
    }))
})
</script>

<template>
  <div
    v-if="company"
    class="bg-white rounded-lg border border-gray-200 shadow-sm overflow-y-auto flex flex-col"
    :class="maxHeight ? 'lg:h-[var(--sidebar-height)]' : ''"
    :style="maxHeight ? { '--sidebar-height': maxHeight } : {}"
  >
    <div
      class="p-6 border-b border-gray-200"
      :style="{ backgroundColor: bgColor }"
    >
      <div class="flex flex-col items-center">
        <div class="w-24 h-24 flex items-center justify-center bg-white rounded-lg mb-4 shadow-sm">
          <img
            :src="proxyImageUrl(company.logo_url || '/img/logo.svg')"
            :alt="company.brand_name || company.company_name"
            width="80"
            height="80"
            class="w-20 h-20 object-contain"
            loading="lazy"
            decoding="async"
            @error="($event.target as HTMLImageElement).src = '/img/logo.svg'"
          >
        </div>
        <h3 class="text-lg font-bold text-gray-900 text-center">
          {{ company.brand_name || company.company_name }}
        </h3>
      </div>
    </div>
    <div
      v-if="company.description"
      class="p-6 border-b border-gray-200"
    >
      <h4 class="text-sm font-semibold text-gray-900 mb-2">
        About
      </h4>
      <div class="overflow-hidden transition-all duration-300 ease-in-out">
        <p class="text-sm text-gray-600 leading-relaxed">
          {{ truncatedDescription }}
        </p>
      </div>
      <Button
        v-if="shouldShowSeeMore"
        variant="ghost"
        color="gray"
        size="xs"
        class="mt-2"
        @click="showFullDescription = !showFullDescription"
      >
        {{ showFullDescription ? 'See less' : 'See more' }}
      </Button>
    </div>
    <div class="p-6">
      <h4 class="text-sm font-semibold text-gray-900 mb-4">
        Company Details
      </h4>
      <div class="space-y-4">
        <div
          v-for="detail in companyDetails"
          :key="detail.label"
          class="flex items-start gap-3"
        >
          <Icon
            :name="detail.icon"
            class="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5"
          />
          <div class="flex-1 min-w-0">
            <p class="text-xs text-gray-500">
              {{ detail.label }}
            </p>
            <a
              v-if="detail.isLink"
              :href="detail.value"
              target="_blank"
              rel="noopener noreferrer"
              class="text-sm text-blue-600 hover:underline break-all"
            >
              {{ detail.value }}
            </a>
            <p
              v-else
              class="text-sm text-gray-900 font-medium break-words"
            >
              {{ detail.value }}
            </p>
          </div>
        </div>
      </div>
      <div
        v-if="socialLinks.length > 0"
        class="mt-6 pt-6 border-t border-gray-200"
      >
        <h4 class="text-sm font-semibold text-gray-900 mb-3">
          Follow Them
        </h4>
        <div class="flex flex-wrap gap-2">
          <a
            v-for="social in socialLinks"
            :key="social.key"
            :href="social.url"
            target="_blank"
            rel="noopener noreferrer"
            class="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            {{ social.label }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>
